import {
    AfterViewInit,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { DashboardResourcesByDateDataLoaderFactory } from './data-loaders/resources-by-date';
import { DashboardResourcesByDayNoDataLoaderFactory } from './data-loaders/resources-by-day-no';
import { BiVizDashboardResourcesSqlMap } from './sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { DecoActivityByDateDataLoaderFactory } from './data-loaders/deco-activity-by-date';
import { DecoActivityByDayNoDataLoaderFactory } from './data-loaders/deco-activity-by-day-no';
import { BiVizDashboardDecoActivitySqlMap } from './sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';
import { MopaResourcesDetailedChartDocs } from './mopa-resources-detailed.chart-documentation';
import { IDashboardContext } from 'src/app/services/query-context/models/dashboard-context.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';

@Component({
    selector: 'app-mopa-resources-detailed',
    templateUrl: './mopa-resources-detailed.component.html',
    styleUrls: ['./mopa-resources-detailed.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaResourcesDetailedComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit, OnDestroy
{
    override DASHBOARD_NAME = 'Resources Detailed';
    override DASHBOARD_ID = 'mopa-resources-detailed';
    values = Object.values;

    /** Latest dashboard context, kept in sync with currentDashboardContext$. */
    private dashboardContext: IDashboardContext = {};
    private dashboardContextSub?: Subscription;

    /** GCP Project id used in the dashboard — resolved from the selected data project */
    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.resources-detailed.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.resources-detailed.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.resources-detailed.filter.activity_app_version':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId: 'mopa.resources-detailed.filter.app_version',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION'
                ),
            }).create(),
        'mopa.resources-detailed.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resources-detailed.filter.firebase_app_store':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.firebase_app_store',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_APP_STORE',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                })
            ).create(),
        'mopa.resources-detailed.filter.firebase_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.firebase_country_tier',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                // filterContext: ['FIREBASE_COUNTRY'],
            }).create(),
        'mopa.resources-detailed.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.resources-detailed.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.resources-detailed.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            }).create(),
        'mopa.resources-detailed.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.attribution_traffic_source',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                })
            ).create(),
        'mopa.resources-detailed.filter.firebase_install_version':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.firebase_install_version',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                    orderByQuery: datasourceColumnOrderByQuery(
                        'FIREBASE_INSTALL_VERSION'
                    ),
                })
            ).create(),
        'mopa.resources-detailed.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resources-detailed.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources-detailed.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
        }).create(),
        'mopa.resources-detailed.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources-detailed.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.resources-detailed.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.attribution_network_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                })
            ).create(),
        'mopa.resources-detailed.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.attribution_campaign_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                })
            ).create(),
        // Array merge form: dropdown unions resource sources (dashboard_resources)
        // and deco sources (dashboard_deco_activity), so the source filter drives both
        // chart families.
        'mopa.resources-detailed.filter.resources_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.resources-detailed.filter.resources_source',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'RESOURCES_SOURCE',
            datasourceSqlMap: [
                BiVizDashboardResourcesSqlMap.sqlMap,
                BiVizDashboardDecoActivitySqlMap.sqlMap,
            ],
            filterContext: true,
        }).create(),
        'mopa.resources-detailed.filter.resource_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources-detailed.filter.resource_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        // Array merge form: dropdown unions resource_payment_type (dashboard_resources)
        // and income_type (dashboard_deco_activity). Each chart loader applies its own
        // table's column in WHERE, so one filter drives both families.
        'mopa.resources-detailed.filter.resource_payment_type':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.resource_payment_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                datasourceSqlMap: [
                    BiVizDashboardResourcesSqlMap.sqlMap,
                    BiVizDashboardDecoActivitySqlMap.sqlMap,
                ],
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'RESOURCE_PAYMENT_TYPE'
                ),
            }).create(),
        'mopa.resources-detailed.filter.resource_source_name':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.resource_source_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'RESOURCE_SOURCE_NAME',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                })
            ).create(),
        'mopa.resources-detailed.filter.resource_source_category':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resources-detailed.filter.resource_source_category',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'RESOURCE_SOURCE_CATEGORY',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                })
            ).create(),
        'mopa.resources-detailed.filter.change_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources-detailed.filter.change_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'CHANGE_TYPE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        // deco_context filters — dropdown values merged across both tables
        // (resources `source_params.ctx_deco_*` ∪ deco-activity `deco_context.*`);
        // one filter drives both resources and deco charts.
        'mopa.resources-detailed.filter.deco_context_rarity':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.deco_context_rarity',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'DECO_CONTEXT_RARITY',
                datasourceSqlMap: [
                    BiVizDashboardResourcesSqlMap.sqlMap,
                    BiVizDashboardDecoActivitySqlMap.sqlMap,
                ],
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'DECO_CONTEXT_RARITY'
                ),
            }).create(),
        'mopa.resources-detailed.filter.deco_context_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.resources-detailed.filter.deco_context_size',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DECO_CONTEXT_SIZE',
            datasourceSqlMap: [
                BiVizDashboardResourcesSqlMap.sqlMap,
                BiVizDashboardDecoActivitySqlMap.sqlMap,
            ],
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('DECO_CONTEXT_SIZE'),
        }).create(),
        'mopa.resources-detailed.filter.deco_context_set_id':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.deco_context_set_id',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'DECO_CONTEXT_SET_ID',
                datasourceSqlMap: [
                    BiVizDashboardResourcesSqlMap.sqlMap,
                    BiVizDashboardDecoActivitySqlMap.sqlMap,
                ],
                filterContext: true,
            }).create(),
        'mopa.resources-detailed.filter.deco_context_inspirations':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.deco_context_inspirations',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'DECO_CONTEXT_INSPIRATIONS',
                datasourceSqlMap: [
                    BiVizDashboardResourcesSqlMap.sqlMap,
                    BiVizDashboardDecoActivitySqlMap.sqlMap,
                ],
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'DECO_CONTEXT_INSPIRATIONS'
                ),
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.resources-detailed.modify_time_users_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.resources-detailed.modify_time_users_datasource',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: BiVizDashboardResourcesSqlMap.sqlMap.dataset,
                tableId: BiVizDashboardResourcesSqlMap.sqlMap.tableId,
            }).create(),
        'mopa.resources-detailed.resources_by_date':
            new DashboardResourcesByDateDataLoaderFactory({
                createdInstanceId: 'mopa.resources-detailed.resources_by_date',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.resources-detailed.resources_by_day_no':
            new DashboardResourcesByDayNoDataLoaderFactory({
                createdInstanceId:
                    'mopa.resources-detailed.resources_by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.resources-detailed.deco_activity_by_date':
            new DecoActivityByDateDataLoaderFactory({
                createdInstanceId:
                    'mopa.resources-detailed.deco_activity_by_date',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.resources-detailed.deco_activity_by_day_no':
            new DecoActivityByDayNoDataLoaderFactory({
                createdInstanceId:
                    'mopa.resources-detailed.deco_activity_by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////    SETTINGS
    /////////////////////////////////////////////////////////////////

    /** Toggle between active_day / install_day for "by day_no" charts. */
    settingDayMode: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_DAY_MODE,
            viewtype: 'toggle',
            displayName: 'Day mode',
            description: 'Affects "by day_no" charts',
            value: 'install_day',
            selectOptions: [
                'install_day',
                // 'active_day'
            ],
        },
    };

    // settingShowAllUsers: ICheckboxSettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.META_SHOW_ALL_USERS,
    //         viewtype: 'checkbox',
    //         displayName: 'All',
    //         value: false,
    //     },
    // };

    // settingShowMetaUsers: ICheckboxSettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.META_SHOW_META_USERS,
    //         viewtype: 'checkbox',
    //         displayName: 'META',
    //         value: true,
    //     },
    // };
    // settingShowHopaUsers: ICheckboxSettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.META_SHOW_HOPA_USERS,
    //         viewtype: 'checkbox',
    //         displayName: 'HOPA',
    //         value: true,
    //     },
    // };
    // settingShowMetaProductiveUsers: ICheckboxSettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS,
    //         viewtype: 'checkbox',
    //         displayName: 'META Prod.',
    //         value: true,
    //     },
    // };
    // settingShowCoreLoopUsers: ICheckboxSettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.META_SHOW_CORE_LOOP_USERS,
    //         viewtype: 'checkbox',
    //         displayName: 'CORE LOOP',
    //         value: true,
    //     },
    // };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.resources-detailed.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.modify_time_users_datasource'
            ].emitter,
    };

    energy_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.energy_inflow',
            dashboardItem: {
                title: 'Energy inflow',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_inflow',
            metricName: 'Energy inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_energy_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.energy_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy inflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.avg_energy_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_inflow_per_active_user',
            metricName: 'Avg. Energy inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_energy_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_energy_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy inflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_avg_energy_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_inflow_per_user',
                metricName: 'Running avg. Energy inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_energy_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_inflow_per_active_user',
            dashboardItem: {
                title: 'Median Energy inflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_energy_inflow_per_active_user',
            metricName: 'Median Energy inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    energy_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.energy_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.energy_inflow_by_day_no',
            dashboardItem: {
                title: 'Energy inflow by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_inflow_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'energy_inflow',
            metricName: 'Energy inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_energy_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_inflow_per_user_by_day_no',
            dashboardItem: {
                title: 'Median Energy inflow per user by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_inflow_per_user_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_energy_inflow_per_user',
            metricName: 'Median Energy inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_energy_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_energy_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_energy_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Energy inflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_median_energy_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_energy_inflow_per_user',
                metricName: 'Running median Energy inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    energy_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.energy_outflow',
            dashboardItem: {
                title: 'Energy outflow',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_outflow',
            metricName: 'Energy outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_energy_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_energy_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_energy_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy outflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.avg_energy_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_outflow_per_active_user',
            metricName: 'Avg. Energy outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_energy_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_energy_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy outflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_avg_energy_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_outflow_per_user',
                metricName: 'Running avg. Energy outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_energy_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_outflow_per_active_user',
            dashboardItem: {
                title: 'Median Energy outflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_energy_outflow_per_active_user',
            metricName: 'Median Energy outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    energy_outflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.energy_outflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.energy_outflow_by_day_no',
            dashboardItem: {
                title: 'Energy outflow by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_outflow_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'energy_outflow',
            metricName: 'Energy outflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_energy_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_outflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_outflow_per_user_by_day_no',
            dashboardItem: {
                title: 'Median Energy outflow per user by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_outflow_per_user_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_energy_outflow_per_user',
            metricName: 'Median Energy outflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_energy_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_energy_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_energy_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Energy outflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_median_energy_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_energy_outflow_per_user',
                metricName: 'Running median Energy outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    energy_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.energy_balance',
            dashboardItem: {
                title: 'Energy balance',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_balance',
            metricName: 'Energy balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_energy_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_energy_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_energy_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy balance per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.avg_energy_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_balance_per_active_user',
            metricName: 'Avg. Energy balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_energy_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_energy_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy balance per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_avg_energy_balance_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_balance_per_user',
                metricName: 'Running avg. Energy balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_energy_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_balance_per_active_user',
            dashboardItem: {
                title: 'Median Energy balance per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_energy_balance_per_active_user',
            metricName: 'Median Energy balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    energy_balance_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.energy_balance_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.energy_balance_by_day_no',
            dashboardItem: {
                title: 'Energy balance by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.energy_balance_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'energy_balance',
            metricName: 'Energy balance by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_energy_balance_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_energy_balance_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_energy_balance_per_user_by_day_no',
            dashboardItem: {
                title: 'Median Energy balance per user by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_energy_balance_per_user_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_energy_balance_per_user',
            metricName: 'Median Energy balance per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_energy_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_energy_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_energy_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Energy balance per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_median_energy_balance_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_energy_balance_per_user',
                metricName: 'Running median Energy balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    gems_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.gems_inflow',
            dashboardItem: { title: 'Gems inflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_inflow',
            metricName: 'Gems inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_gems_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_gems_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Gems inflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_inflow_per_active_user',
            metricName: 'Avg. Gems inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_gems_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.running_avg_gems_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.running_avg_gems_inflow_per_user_by_day_no',
            dashboardItem: {
                title: 'Running avg. Gems inflow per user by day no',
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'running_avg_gems_inflow_per_user',
            metricName: 'Running avg. Gems inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_gems_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_inflow_per_active_user',
            dashboardItem: { title: 'Median Gems inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_gems_inflow_per_active_user',
            metricName: 'Median Gems inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    gems_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.gems_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.gems_inflow_by_day_no',
            dashboardItem: { title: 'Gems inflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'gems_inflow',
            metricName: 'Gems inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_gems_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_inflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Gems inflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_gems_inflow_per_user',
            metricName: 'Median Gems inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_gems_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_gems_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_gems_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Gems inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_gems_inflow_per_user',
                metricName: 'Running median Gems inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    gems_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.gems_outflow',
            dashboardItem: { title: 'Gems outflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_outflow',
            metricName: 'Gems outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_gems_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_gems_outflow_per_active_user',
            dashboardItem: { title: 'Avg. Gems outflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_outflow_per_active_user',
            metricName: 'Avg. Gems outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_gems_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_gems_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_gems_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Gems outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_gems_outflow_per_user',
                metricName: 'Running avg. Gems outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_gems_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_outflow_per_active_user',
            dashboardItem: { title: 'Median Gems outflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_gems_outflow_per_active_user',
            metricName: 'Median Gems outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    gems_outflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.gems_outflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.gems_outflow_by_day_no',
            dashboardItem: { title: 'Gems outflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'gems_outflow',
            metricName: 'Gems outflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_gems_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_outflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_outflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Gems outflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_gems_outflow_per_user',
            metricName: 'Median Gems outflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_gems_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_gems_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_gems_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Gems outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_gems_outflow_per_user',
                metricName: 'Running median Gems outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    gems_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.gems_balance',
            dashboardItem: { title: 'Gems balance' },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_balance',
            metricName: 'Gems balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_gems_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_gems_balance_per_active_user',
            dashboardItem: { title: 'Avg. Gems balance per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_balance_per_active_user',
            metricName: 'Avg. Gems balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_gems_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_gems_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_gems_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Gems balance per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_gems_balance_per_user',
                metricName: 'Running avg. Gems balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_gems_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_balance_per_active_user',
            dashboardItem: { title: 'Median Gems balance per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_gems_balance_per_active_user',
            metricName: 'Median Gems balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    gems_balance_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.gems_balance_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.gems_balance_by_day_no',
            dashboardItem: { title: 'Gems balance by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'gems_balance',
            metricName: 'Gems balance by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_gems_balance_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_gems_balance_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_gems_balance_per_user_by_day_no',
            dashboardItem: { title: 'Median Gems balance per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_gems_balance_per_user',
            metricName: 'Median Gems balance per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_gems_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_gems_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_gems_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Gems balance per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_gems_balance_per_user',
                metricName: 'Running median Gems balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    hints_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.hints_inflow',
            dashboardItem: { title: 'Hints inflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_inflow',
            metricName: 'Hints inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_hints_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_hints_inflow_per_active_user',
            dashboardItem: { title: 'Avg. Hints inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_inflow_per_active_user',
            metricName: 'Avg. Hints inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_hints_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_hints_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_inflow_per_user',
                metricName: 'Running avg. Hints inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_hints_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_inflow_per_active_user',
            dashboardItem: { title: 'Median Hints inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_hints_inflow_per_active_user',
            metricName: 'Median Hints inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    hints_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.hints_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.hints_inflow_by_day_no',
            dashboardItem: { title: 'Hints inflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'hints_inflow',
            metricName: 'Hints inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_hints_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_inflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Hints inflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_hints_inflow_per_user',
            metricName: 'Median Hints inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_hints_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_hints_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_hints_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Hints inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_hints_inflow_per_user',
                metricName: 'Running median Hints inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    hints_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.hints_outflow',
            dashboardItem: { title: 'Hints outflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_outflow',
            metricName: 'Hints outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_hints_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_hints_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Hints outflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_outflow_per_active_user',
            metricName: 'Avg. Hints outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_hints_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_hints_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_outflow_per_user',
                metricName: 'Running avg. Hints outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_hints_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_outflow_per_active_user',
            dashboardItem: { title: 'Median Hints outflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_hints_outflow_per_active_user',
            metricName: 'Median Hints outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    hints_outflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.hints_outflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.hints_outflow_by_day_no',
            dashboardItem: { title: 'Hints outflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'hints_outflow',
            metricName: 'Hints outflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_hints_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_outflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_outflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Hints outflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_hints_outflow_per_user',
            metricName: 'Median Hints outflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_hints_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_hints_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_hints_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Hints outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_hints_outflow_per_user',
                metricName: 'Running median Hints outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    hints_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.hints_balance',
            dashboardItem: { title: 'Hints balance' },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_balance',
            metricName: 'Hints balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_hints_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_hints_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Hints balance per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_balance_per_active_user',
            metricName: 'Avg. Hints balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_hints_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_hints_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints balance per user by day no',
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_balance_per_user',
                metricName: 'Running avg. Hints balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_hints_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_balance_per_active_user',
            dashboardItem: { title: 'Median Hints balance per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_hints_balance_per_active_user',
            metricName: 'Median Hints balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    hints_balance_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.hints_balance_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.hints_balance_by_day_no',
            dashboardItem: { title: 'Hints balance by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'hints_balance',
            metricName: 'Hints balance by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_hints_balance_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_hints_balance_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_hints_balance_per_user_by_day_no',
            dashboardItem: { title: 'Median Hints balance per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_hints_balance_per_user',
            metricName: 'Median Hints balance per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_hints_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_hints_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_hints_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Hints balance per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_hints_balance_per_user',
                metricName: 'Running median Hints balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    coins_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.coins_inflow',
            dashboardItem: { title: 'Coins inflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_inflow',
            metricName: 'Coinsinflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_coins_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_coins_inflow_per_active_user',
            dashboardItem: { title: 'Avg. Coins inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_inflow_per_active_user',
            metricName: 'Avg. Coins inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_coins_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_coins_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_inflow_per_user',
                metricName: 'Running avg. Coins inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_coins_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_inflow_per_active_user',
            dashboardItem: { title: 'Median Coins inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_coins_inflow_per_active_user',
            metricName: 'Median Coins inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    coins_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.coins_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.coins_inflow_by_day_no',
            dashboardItem: { title: 'Coins inflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'coins_inflow',
            metricName: 'Coins inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_coins_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_inflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Coins inflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_coins_inflow_per_user',
            metricName: 'Median Coins inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_coins_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_coins_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_coins_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Coins inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_coins_inflow_per_user',
                metricName: 'Running median Coins inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    coins_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.coins_outflow',
            dashboardItem: { title: 'Coins outflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_outflow',
            metricName: 'Coins outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_coins_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_coins_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Coins outflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_outflow_per_active_user',
            metricName: 'Avg. Coins outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_coins_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_coins_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_outflow_per_user',
                metricName: 'Running avg. Coins outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_coins_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_outflow_per_active_user',
            dashboardItem: { title: 'Median Coins outflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_coins_outflow_per_active_user',
            metricName: 'Median Coins outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    coins_outflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.coins_outflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.coins_outflow_by_day_no',
            dashboardItem: { title: 'Coins outflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'coins_outflow',
            metricName: 'Coins outflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_coins_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_outflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_outflow_per_user_by_day_no',
            dashboardItem: { title: 'Median Coins outflow per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_coins_outflow_per_user',
            metricName: 'Median Coins outflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_coins_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_coins_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_coins_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Coins outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_coins_outflow_per_user',
                metricName: 'Running median Coins outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    coins_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.coins_balance',
            dashboardItem: { title: 'Coins balance' },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_balance',
            metricName: 'Coins balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_coins_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_coins_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Coins balance per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_balance_per_active_user',
            metricName: 'Avg. Coins balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_coins_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_coins_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins balance per user by day no',
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_balance_per_user',
                metricName: 'Running avg. Coins balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_coins_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_balance_per_active_user',
            dashboardItem: { title: 'Median Coins balance per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_coins_balance_per_active_user',
            metricName: 'Median Coins balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    coins_balance_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.coins_balance_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.coins_balance_by_day_no',
            dashboardItem: { title: 'Coins balance by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'coins_balance',
            metricName: 'Coins balance by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_coins_balance_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_coins_balance_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_coins_balance_per_user_by_day_no',
            dashboardItem: { title: 'Median Coins balance per user by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_coins_balance_per_user',
            metricName: 'Median Coins balance per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_coins_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_coins_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_coins_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Coins balance per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_coins_balance_per_user',
                metricName: 'Running median Coins balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    compasses_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.compasses_inflow',
            dashboardItem: { title: 'Compasses inflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_inflow',
            metricName: 'Compasses inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_compasses_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_compasses_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses inflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_inflow_per_active_user',
            metricName: 'Avg. Compasses inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_compasses_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_compasses_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_inflow_per_user',
                metricName: 'Running avg. Compasses inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_compasses_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_compasses_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_compasses_inflow_per_active_user',
            dashboardItem: { title: 'Median Compasses inflow per active user' },
            xColumnLabel: 'date',
            yColumnLabel: 'median_compasses_inflow_per_active_user',
            metricName: 'Median Compasses inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    compasses_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.compasses_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.compasses_inflow_by_day_no',
            dashboardItem: { title: 'Compasses inflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'compasses_inflow',
            metricName: 'Compasses inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_compasses_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_compasses_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_compasses_inflow_per_user_by_day_no',
            dashboardItem: {
                title: 'Median Compasses inflow per user by day no',
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_compasses_inflow_per_user',
            metricName: 'Median Compasses inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    running_median_compasses_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_compasses_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_compasses_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Compasses inflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_compasses_inflow_per_user',
                metricName: 'Running median Compasses inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    compasses_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.compasses_outflow',
            dashboardItem: { title: 'Compasses outflow' },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_outflow',
            metricName: 'Compasses outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_compasses_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_compasses_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses outflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_outflow_per_active_user',
            metricName: 'Avg. Compasses outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_compasses_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_compasses_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_outflow_per_user',
                metricName: 'Running avg. Compasses outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_compasses_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_compasses_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_compasses_outflow_per_active_user',
            dashboardItem: {
                title: 'Median Compasses outflow per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_compasses_outflow_per_active_user',
            metricName: 'Median Compasses outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    compasses_outflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.compasses_outflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.compasses_outflow_by_day_no',
            dashboardItem: { title: 'Compasses outflow by day no' },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'compasses_outflow',
            metricName: 'Compasses outflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_compasses_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.median_compasses_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.median_compasses_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Median Compasses outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'median_compasses_outflow_per_user',
                metricName: 'Median Compasses outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    running_median_compasses_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_compasses_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_compasses_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Compasses outflow per user by day no',
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_compasses_outflow_per_user',
                metricName: 'Running median Compasses outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    compasses_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.compasses_balance',
            dashboardItem: { title: 'Compasses balance' },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_balance',
            metricName: 'Compasses balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    avg_compasses_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_compasses_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses balance per active user',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_balance_per_active_user',
            metricName: 'Avg. Compasses balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    running_avg_compasses_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_compasses_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses balance per user by day no',
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_balance_per_user',
                metricName: 'Running avg. Compasses balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    median_compasses_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_compasses_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_compasses_balance_per_active_user',
            dashboardItem: {
                title: 'Median Compasses balance per active user',
            },
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_compasses_balance_per_active_user',
            metricName: 'Median Compasses balance per active user',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_date']
                .emitter,
    };

    compasses_balance_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.compasses_balance_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.compasses_balance_by_day_no',
            dashboardItem: {
                title: 'Compasses balance by day no',
            },
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'compasses_balance',
            metricName: 'Compasses balance',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources-detailed.resources_by_day_no']
                .emitter,
    };

    median_compasses_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.median_compasses_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.median_compasses_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Median Compasses balance per user by day no',
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'median_compasses_balance_per_user',
                metricName: 'Median Compasses balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    running_median_compasses_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_compasses_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_compasses_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Compasses balance per user by day no',
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_compasses_balance_per_user',
                metricName: 'Running median Compasses balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.resources_by_day_no'
                ].emitter,
        };

    /////////////////////////////////////////////////////////////////
    ////    DECORATIONS — Decoration inflow
    /////////////////////////////////////////////////////////////////

    decoration_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.decoration_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.decoration_inflow',
            dashboardItem: {
                title: 'Decoration inflow',
                chartDocumentation: MopaResourcesDetailedChartDocs.decoration_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'decoration_inflow',
            metricName: 'Decoration inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    decoration_inflow_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.decoration_inflow_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.decoration_inflow_by_day_no',
            dashboardItem: {
                title: 'Decoration inflow by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.decoration_inflow_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'decoration_inflow',
            metricName: 'Decoration inflow by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_day_no'
            ].emitter,
    };

    avg_decoration_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.avg_decoration_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_decoration_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Decoration inflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.avg_decoration_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_decoration_inflow_per_active_user',
            metricName: 'Avg. Decoration inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    median_decoration_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_decoration_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_decoration_inflow_per_active_user',
            dashboardItem: {
                title: 'Median Decoration inflow per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_decoration_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_decoration_inflow_per_active_user',
            metricName: 'Median Decoration inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    median_decoration_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.median_decoration_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.median_decoration_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Median Decoration inflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.median_decoration_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'median_decoration_inflow_per_user',
                metricName: 'Median Decoration inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.deco_activity_by_day_no'
                ].emitter,
        };

    running_avg_decoration_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_avg_decoration_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_decoration_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Decoration inflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_avg_decoration_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_decoration_inflow_per_user',
                metricName: 'Running avg. Decoration inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.deco_activity_by_day_no'
                ].emitter,
        };

    running_median_decoration_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_decoration_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_decoration_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median Decoration inflow per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_median_decoration_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_decoration_inflow_per_user',
                metricName: 'Running median Decoration inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.deco_activity_by_day_no'
                ].emitter,
        };

    /////////////////////////////////////////////////////////////////
    ////    DECORATIONS — New decos placed
    /////////////////////////////////////////////////////////////////

    new_decos_placed: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.new_decos_placed',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources-detailed.chart.new_decos_placed',
            dashboardItem: {
                title: 'New decos placed',
                chartDocumentation: MopaResourcesDetailedChartDocs.new_decos_placed,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_decos_placed',
            metricName: 'New decos placed',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    new_decos_placed_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.new_decos_placed_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.new_decos_placed_by_day_no',
            dashboardItem: {
                title: 'New decos placed by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.new_decos_placed_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'new_decos_placed',
            metricName: 'New decos placed by day no',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_day_no'
            ].emitter,
    };

    avg_new_decos_placed_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.avg_new_decos_placed_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.avg_new_decos_placed_per_active_user',
            dashboardItem: {
                title: 'Avg. New decos placed per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.avg_new_decos_placed_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_new_decos_placed_per_active_user',
            metricName: 'Avg. New decos placed per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    median_new_decos_placed_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_new_decos_placed_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_new_decos_placed_per_active_user',
            dashboardItem: {
                title: 'Median New decos placed per active user',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_new_decos_placed_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'median_new_decos_placed_per_active_user',
            metricName: 'Median New decos placed per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_date'
            ].emitter,
    };

    median_new_decos_placed_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources-detailed.chart.median_new_decos_placed_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources-detailed.chart.median_new_decos_placed_per_user_by_day_no',
            dashboardItem: {
                title: 'Median New decos placed per user by day no',
                chartDocumentation: MopaResourcesDetailedChartDocs.median_new_decos_placed_per_user_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'median_new_decos_placed_per_user',
            metricName: 'Median New decos placed per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resources-detailed.deco_activity_by_day_no'
            ].emitter,
    };

    running_avg_new_decos_placed_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_avg_new_decos_placed_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_avg_new_decos_placed_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. New decos placed per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_avg_new_decos_placed_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_new_decos_placed_per_user',
                metricName: 'Running avg. New decos placed per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.deco_activity_by_day_no'
                ].emitter,
        };

    running_median_new_decos_placed_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources-detailed.chart.running_median_new_decos_placed_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources-detailed.chart.running_median_new_decos_placed_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running median New decos placed per user by day no',
                    chartDocumentation: MopaResourcesDetailedChartDocs.running_median_new_decos_placed_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_median_new_decos_placed_per_user',
                metricName: 'Running median New decos placed per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.resources-detailed.deco_activity_by_day_no'
                ].emitter,
        };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        RESOURCE_NAME: {
            id: 'mopa.resources-detailed.filter.resource_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.resource_name',
                    'RESOURCE_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.resource_name'
                ].emitter,
        },
        CHANGE_TYPE: {
            id: 'mopa.resources-detailed.filter.change_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.change_type',
                    'CHANGE_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.change_type'
                ].emitter,
        },
        RESOURCE_PAYMENT_TYPE: {
            id: 'mopa.resources-detailed.filter.resource_payment_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.resource_payment_type',
                    'RESOURCE_PAYMENT_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.resource_payment_type'
                ].emitter,
        },
        RESOURCE_SOURCE_CATEGORY: {
            id: 'mopa.resources-detailed.filter.resource_source_category',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.resource_source_category',
                    'RESOURCE_SOURCE_CATEGORY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.resource_source_category'
                ].emitter,
        },
        RESOURCE_SOURCE_NAME: {
            id: 'mopa.resources-detailed.filter.resource_source_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.resource_source_name',
                    'RESOURCE_SOURCE_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.resource_source_name'
                ].emitter,
        },
        RESOURCES_SOURCE: {
            id: 'mopa.resources-detailed.filter.resources_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.resources_source',
                    'RESOURCES_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.resources_source'
                ].emitter,
        },
        DECO_CONTEXT_RARITY: {
            id: 'mopa.resources-detailed.filter.deco_context_rarity',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.deco_context_rarity',
                    'DECO_CONTEXT_RARITY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.deco_context_rarity'
                ].emitter,
        },
        DECO_CONTEXT_SIZE: {
            id: 'mopa.resources-detailed.filter.deco_context_size',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.deco_context_size',
                    'DECO_CONTEXT_SIZE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.deco_context_size'
                ].emitter,
        },
        DECO_CONTEXT_SET_ID: {
            id: 'mopa.resources-detailed.filter.deco_context_set_id',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.deco_context_set_id',
                    'DECO_CONTEXT_SET_ID'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.deco_context_set_id'
                ].emitter,
        },
        DECO_CONTEXT_INSPIRATIONS: {
            id: 'mopa.resources-detailed.filter.deco_context_inspirations',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.deco_context_inspirations',
                    'DECO_CONTEXT_INSPIRATIONS'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.deco_context_inspirations'
                ].emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.resources-detailed.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.resources-detailed.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.resources-detailed.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.activity_app_version'
                ].emitter,
        },
        // DAILY_TOTAL_ENERGY_SPENT: {
        //     id: 'mopa.resources-detailed.filter.daily_total_energy_spent',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'mopa.resources-detailed.filter.daily_total_energy_spent',
        //         datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'mopa.resources-detailed.filter.daily_total_energy_spent'
        //         ].emitter,
        // },
        SEGMENT: {
            id: 'mopa.resources-detailed.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.resources-detailed.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources-detailed.filter.segment']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.resources-detailed.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.resources-detailed.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.resources-detailed.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.resources-detailed.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.resources-detailed.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.resources-detailed.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_country'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.resources-detailed.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.resources-detailed.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.resources-detailed.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.resources-detailed.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.etl_payer'
                ].emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.resources-detailed.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.resources-detailed.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.resources-detailed.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources-detailed.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources-detailed.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaResourcesComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });

        this.dashboardContextSub =
            this.dashboardService.currentDashboardContext$.subscribe((ctx) => {
                this.dashboardContext = ctx ?? {};
            });
    }

    ngOnDestroy(): void {
        this.dashboardContextSub?.unsubscribe();
    }

    /** Returns the persisted collapse state of a section (default: expanded). */
    isSectionCollapsed(id: string): boolean {
        return this.dashboardContext.collapsedSections?.[id] ?? false;
    }

    /** Persists the collapse state of a section via the dashboard context. */
    onSectionActiveChange(id: string, active: boolean): void {
        const collapsedSections = {
            ...(this.dashboardContext.collapsedSections ?? {}),
        };
        collapsedSections[id] = !active;
        this.dashboardService.setDashboardContext(
            { ...this.dashboardContext, collapsedSections },
            true
        );
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaResourcesComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 180 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const FirebaseFirstOpenDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_FIRST_OPEN_DATE',
                state: {
                    value: {
                        preset: 'last 180 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
            FirebaseFirstOpenDateIC,
        ];

        return initialContext;
    }
}
